import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { timer } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { JobTrackingService } from '@core/services/job-tracking.service';
import { JobTrackingStatus, JobTrackingType } from '@core/services/job.service';
import { UserNotificationService } from '@core/services/user-notification.service';
import { UiTranslationService } from '@core/services/ui-translation.service';

@Component({
  selector: 'app-notification-dropdown',
  imports: [NgbDropdownModule],
  providers: [UserNotificationService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './notification-dropdown.html',
  styleUrl: './notification-dropdown.scss',
})
export class NotificationDropdown {
  readonly notifications = inject(UserNotificationService);
  readonly i18n = inject(UiTranslationService).namespace('notifications');

  private readonly destroyRef = inject(DestroyRef);
  private readonly toastr = inject(ToastrService);
  private readonly jobTrackingService = inject(JobTrackingService);
  private readonly deckTranslations =
    inject(UiTranslationService).namespace('deck');
  private readonly displayedTerminalToasts = new Set<number>();
  readonly jobActiveCount = this.jobTrackingService.activeCount;
  readonly jobProgressAverage = this.jobTrackingService.progressAverage;
  readonly jobActiveJobs = this.jobTrackingService.activeJobs;

  private initializeJobs(): void {
    this.jobTrackingService.ensurePolling();
    this.jobTrackingService.terminalEvents$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (this.displayedTerminalToasts.has(event.jobId)) {
          return;
        }

        this.displayedTerminalToasts.add(event.jobId);

        if (event.status === 'completed') {
          this.toastr.success(this.tr('common.jobTracking.completed'));
          return;
        }

        if (event.status === 'failed') {
          this.toastr.error(this.tr('common.jobTracking.failed'));
          return;
        }

        this.toastr.warning(this.tr('common.jobTracking.cancelled'));
      });
  }

  tr(key: string, params?: Record<string, string | number>): string {
    return this.deckTranslations.tr(key, params);
  }

  jobTypeLabel(jobType: JobTrackingType): string {
    return this.tr(`common.jobTracking.type.${jobType}`);
  }

  jobStatusLabel(status: JobTrackingStatus): string {
    return this.tr(`common.jobTracking.status.${status}`);
  }

  readonly totalCount = computed(
    () => this.jobActiveCount() + this.notifications.notifications().length,
  );

  constructor() {
    this.initializeJobs();
    timer(0, 15000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.notifications.load());
  }
}
